/*
  Shows the mobile buy bar once the real Add to cart button has scrolled out of view, and gets
  out of the way when the on-screen keyboard is up. See snippets/sticky-atc.liquid for why the
  bar contains a real submit button rather than a click proxy.

  Studio templates render snippets/sticky-studio.liquid instead: the same bar with a link in
  place of the button, targeting the Engraving Studio entry link. Everything here is shared; the
  link bar names its own price node and mirrors the entry's href and aria-disabled.
*/
(function () {
  'use strict';

  function init(bar) {
    if (bar.dataset.ready === 'true') return;
    bar.dataset.ready = 'true';

    var anchor = document.getElementById(bar.dataset.target);
    if (!anchor) return;

    // The studio bar carries a link, not a submit button (snippets/sticky-studio.liquid). It
    // follows the entry link it targets: href and aria-disabled are copied over on every change,
    // so the bar disables while the entry is loading, checking, without a variant or errored,
    // and can never open a studio the entry itself would refuse.
    var link = bar.querySelector('[data-je-buybar-link]');
    var linkSource = link && anchor.hasAttribute('data-studio-entry-link') ? anchor : null;

    function syncLink() {
      var href = linkSource.getAttribute('href');
      if (href && linkSource.getAttribute('aria-disabled') !== 'true') {
        link.setAttribute('href', href);
        link.removeAttribute('aria-disabled');
      } else {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
      }
    }

    if (linkSource) {
      syncLink();
      new MutationObserver(syncLink).observe(linkSource, { attributes: true, attributeFilter: ['href', 'aria-disabled'] });
      link.addEventListener('click', function (event) {
        if (link.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          return;
        }
        // Optional chaining on purpose: Shopify.analytics is not on every page this script loads on.
        globalThis.Shopify?.analytics?.publish?.('studio_entry_click', { productId: bar.dataset.productId, arm: bar.dataset.arm, source: 'sticky' });
      });
    }

    // Reserve room at the end of the page so the bar never covers the last row of content.
    document.body.classList.add('template-product-has-buybar');

    var anchorVisible = true;
    // Don't greet people with a buy bar over the hero. It earns its place once they have
    // scrolled into the form, and then stays until the real button is on screen.
    var media = document.querySelector('.product__media-wrapper');
    function pastTheProductShot() {
      if (!media) return window.scrollY > window.innerHeight * 0.75;
      var rect = media.getBoundingClientRect();
      return rect.bottom < window.innerHeight * 0.5;
    }

    // Tabbing onto the anchor while it straddles the bottom edge scrolls it (or leaves it) inside
    // the 80px band the observer excludes, so the observer reports it hidden and the bar would
    // paint over the very control that has focus. Checked inside paint() rather than in a focus
    // handler alone, because the observer and scroll repaints that follow the focus scroll would
    // otherwise re-show the bar. :focus-visible keeps the bar for a mouse user who clicked the
    // anchor and scrolled on.
    function anchorHasKeyboardFocus() {
      try { return anchor.matches(':focus-visible'); } catch (e) { return document.activeElement === anchor; }
    }

    function paint() {
      // The keyboard shrinks the visual viewport; a bar pinned to the layout viewport would
      // either ride on top of the keyboard or cover the field being typed into.
      var vv = window.visualViewport;
      var keyboardUp = vv ? vv.height < window.innerHeight - 150 : false;
      bar.hidden = anchorVisible || keyboardUp || anchorHasKeyboardFocus() || !pastTheProductShot();
    }

    anchor.addEventListener('focus', paint);
    anchor.addEventListener('blur', paint);

    new IntersectionObserver(
      function (entries) {
        anchorVisible = entries[0].isIntersecting;
        paint();
      },
      { rootMargin: '0px 0px -80px 0px' }
    ).observe(anchor);

    var ticking = false;
    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          paint();
        });
      },
      { passive: true }
    );

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', paint);
    }

    paint();

    // Dawn re-renders the price node on every variant change; mirror it into the bar. The form
    // bar derives the node's id from its target; the link bar names it outright.
    var priceId = bar.dataset.priceSource || ('price-' + bar.dataset.target.replace('ProductSubmitButton-', ''));
    var source = document.getElementById(priceId);
    var mirror = bar.querySelector('.je-buybar__price');
    if (source && mirror) {
      new MutationObserver(function () {
        var price = source.querySelector('.price-item--sale, .price-item--regular');
        if (price) mirror.textContent = price.textContent.trim();
      }).observe(source, { childList: true, subtree: true, characterData: true });
    }

    // Keep the thumbnail on the selected variant. It sits an inch from the colour swatch that was
    // just tapped, so a stale picture reads as the wrong product rather than as a generic shot.
    var thumb = bar.querySelector('[data-je-buybar-thumb]');
    var mapEl = bar.querySelector('[data-je-buybar-media]');
    var mediaByVariant = {};
    if (mapEl) {
      try {
        mediaByVariant = JSON.parse(mapEl.textContent) || {};
      } catch (e) {
        mediaByVariant = {};
      }
    }

    var submit = bar.querySelector('button[form]');
    var formEl = submit ? document.getElementById(submit.getAttribute('form')) : null;

    function syncThumb(variantId) {
      if (!thumb) return;
      var id = variantId;
      if (!id) {
        var input = formEl ? formEl.querySelector('[name="id"]') : null;
        id = input ? input.value : '';
      }
      var url = mediaByVariant[id];
      // No entry means nothing better is known than what is already showing, so leave it be
      // rather than blank the image out.
      if (url && thumb.getAttribute('src') !== url) thumb.setAttribute('src', url);
    }

    // Timing is the whole difficulty here. Dawn fetches the section before it swaps the hidden
    // variant id, so a plain change listener on the form still reads the previous variant. This
    // event is published after that swap, and carries the new variant with it.
    if (typeof subscribe === 'function') {
      subscribe('variant-change', function (event) {
        var v = event && event.data && event.data.variant;
        syncThumb(v && v.id ? String(v.id) : '');
      });
    } else if (formEl) {
      // No pubsub: re-read a few times while Dawn catches up. Crude, but it only runs if the
      // theme's own event has gone missing.
      formEl.addEventListener('change', function () {
        [120, 400, 900].forEach(function (ms) {
          setTimeout(function () {
            syncThumb();
          }, ms);
        });
      });
    }

    syncThumb();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-je-buybar]'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
